import {inject, Injectable} from '@angular/core';
import {AuthenticationService} from '@modules/authentication';
import {AnyLinkType} from 'shared/pretty-easy-navigation/models';
import {PrettyEasyNavigationService} from 'shared/pretty-easy-navigation/pretty-easy-navigation.service';
import {TrainerMenu, LearnerMenu, GuestMenu, ParentMenu} from './models';
import {CommunicationCenterService} from '@modules/communication-center';

@Injectable({
  providedIn: 'root'
})
export class PrettyEasyMenuInitializerService {
    menus = {
        trainer: TrainerMenu,
        learner: LearnerMenu,
        guest: GuestMenu,
        parent: ParentMenu,
    } as const;

    private prettyEasyNavigationService = inject(PrettyEasyNavigationService);
    private authService = inject(AuthenticationService);
    private communicationCenter = inject(CommunicationCenterService);

    public initializeMenu(): void {
        this.prettyEasyNavigationService.setMenu(this.menus.guest)
    }

    public setMenu(menuName: keyof typeof this.menus): void {
        const menu = this.menus[menuName];
        if (menu) {
            // Remove imageLink items if the user has no associated community
            if (!this.authService?.userData?.get('collectivite')) {
                this.removeImageLinks(menu);
            }
            // Edit menu item properties
            this.editMenuIfNeeded(menu);
            this.prettyEasyNavigationService.setMenu(menu);
        } else {
            console.warn(`Menu with name ${menuName} not found.`);
        }
    }

    private removeImageLinks(menu: AnyLinkType[]): void {
        menu.slice().forEach(item => {
            // Remove imageLink item
            if (item.type === 'imageLink') {
                const idx = menu.indexOf(item);
                if (idx !== -1) {
                    menu.splice(idx, 1);
                }
                return;
            }
            // Remove imageLink item recursively inside linkGroup or accordionLinkGroup item
            if (item.type === 'linkGroup' || item.type === 'accordionLinkGroup') {
                this.removeImageLinks(item.links);
                // If group has no links left, remove the group entirely
                if (!item.links || item.links.length === 0) {
                    const idx = menu.indexOf(item);
                    if (idx !== -1) {
                        menu.splice(idx, 1);
                    }
                }
            }
        });
    }

    private editMenuIfNeeded(menu: AnyLinkType[]): void {
        const flattenedMenu = this.flattenMenu(menu);
        flattenedMenu.forEach(item => {
            if (item.type === 'link') {
                switch (item.link) {
                    case '{{USER_CORPUS_LINK}}':
                        item.link = `/corpus/user/${(this.authService.userData?.id)}`;
                        break;
                }
            }
            if (item.type === 'imageLink') {
                switch (item.src) {
                    case '{{OFFERED_BY_SRC}}':
                        item.src = this.authService?.userData?.get('collectivite')?.logo_url ?? '';
                        break;
                }
                switch (item.actionToken) {
                    case '{{OFFERED_BY_ACTION}}':
                        item.action = (_event: MouseEvent) => {
                            void _event; // Mark parameter as intentionally unused
                            this.communicationCenter.getRoom('edito').getSubject('showEdito').next(true);
                        };
                        break;
                }
            }
        })
    }

    private flattenMenu(menu: AnyLinkType[]): AnyLinkType[] {
        return menu.reduce((acc: AnyLinkType[], item) => {
            acc.push(item);
            if (item.type === 'linkGroup'
            || item.type === 'accordionLinkGroup')
                acc.push(...this.flattenMenu(item.links));
            return acc;
        }, []);
    }
}
